import { DateTime } from 'luxon';
import moment from 'moment';
import { TaskHelpers } from 'ontraccr-common';

import { msToHours } from '../../../helpers/time';
import { formatProjectLabelFromCompanySettings } from '../../../projects/projectHelpers';
import { applyNightShiftPremium, calculateCostFromHours } from '../../../helpers/payroll';

import {
  getDateKey,
  getDivision,
  getDerivedTasksInRange,
  isGoodTask,
} from '../../reportHelpers';

import { getRelevantWageAndBillingRate } from '../../../helpers/wageHelpers';

import {
  DATE_FORMAT,
} from './staffTimesheets.constants';

export default {};

const isMoment = (d) => d && moment.isMoment(d);

export const getDateArray = (dateRange) => {
  if (dateRange.length < 2 || !isMoment(dateRange[0]) || !isMoment(dateRange[1])) return [];
  const startDate = dateRange[0].clone();
  const endDate = dateRange[1];
  const dArr = [];
  while (startDate.isBefore(endDate)) {
    dArr.push(startDate.format(DATE_FORMAT));
    startDate.add(1, 'd');
  }
  return dArr;
};

export const computeData = ({
  onDataChanged,
  onFiltersChanged,
  userDivisions,
  isRounded,
  settings,
  timeEntryUserMap = {},
  dateRange,
  users = [],
  projects,
  costcodes,
  zeroHoursEnabled,
  isValidTask,
  getAggregateKey,
  divisionMap,
  projectIdMap,
  costcodeIdMap,
  classIdMap,
  dates,
  showActiveUsersOnly = false,
  entityOTProfileMap = {},
}) => {
  const data = [];
  const dateAggregate = {};
  const userDateCostCodeSet = new Set();
  const filteredUsers = showActiveUsersOnly ? users.filter((user) => user.active) : users;

  // zero hours
  if (zeroHoursEnabled) {
    const usersWithNoHours = [];

    filteredUsers.forEach((user) => {
      if (!user.active) return;
      const tasks = timeEntryUserMap[user?.id] ?? [];

      const validTasks = tasks.filter((task) => (
        isGoodTask(task)
          && isValidTask(task, true)
      ));

      // if user has any valid tasks, then they have time logged
      if (!validTasks.length) {
        usersWithNoHours.push(user);
      }
    });

    const aggKey = getAggregateKey('None', 'None', null);

    const usersWithNoHoursData = usersWithNoHours.map((user) => ({
      name: user.name,
      employeeId: user.employeeId || '',
      division: getDivision({
        project: {},
        divisionMap,
        userDivisions,
        user,
      }),
      key: user.id + aggKey,
      totalHours: 0,
    }));

    onDataChanged(usersWithNoHoursData);
    onFiltersChanged({
      users: filteredUsers,
      projects,
      costcodes,
    });
    return usersWithNoHoursData;
  }

  // non zero-hours
  filteredUsers.forEach((user) => {
    const tasks = timeEntryUserMap[user?.id] ?? [];
    getDerivedTasksInRange({
      tasks,
      companySettings: settings,
      settingsMap: entityOTProfileMap,
      dateRange,
      shouldIncludeZeroTimePTO: true,
      isRounded,
    }).forEach((task) => {
      if (!isValidTask(task)) return;

      const project = projectIdMap[task.projectId];
      const costcode = costcodeIdMap[task.costcodeId];
      const unionClass = classIdMap[task.classId];
      const dateDT = TaskHelpers.getTaskDate(task);
      const date = getDateKey({
        ...task,
        endTime: dateDT?.toMillis() ?? null,
      }, DATE_FORMAT);
      const runtimes = TaskHelpers.getRuntimes(task, false);
      const runTime = runtimes.regularTime + runtimes.overtime + runtimes.doubleOT + runtimes.pto;
      const totalHours = msToHours(runTime);
      const regularHours = Math.min(8.0, totalHours);
      const otHours = (totalHours - regularHours);

      const projectLabel = project ? formatProjectLabelFromCompanySettings({
        name: project.name,
        number: project.number,
        settings,
      }) : null;

      const dataObject = {
        name: user.name,
        employeeId: user.employeeId || '',
        division: getDivision({
          project, divisionMap, userDivisions, user,
        }),
        project: projectLabel,
        costcode: costcode ? costcode.code : null,
        regularHours,
        otHours,
        totalHours,
        wage: user.wage,
      };

      if (!(user.id in dateAggregate)) {
        dateAggregate[user.id] = {
          ...dataObject,
          values: {},
        };
      }
      const userObj = dateAggregate[user.id];
      const costCode = costcode ? costcode.code : 'None';
      const aggKey = getAggregateKey(projectLabel ?? 'None', costCode, task.serviceLocation);

      const userDateCostCodeKey = `${user.id}-${date}-${costcode?.id}`;

      const {
        hourlyWage: resolvedHourlyWage,
        dailyWage: resolvedDailyWage,
      } = getRelevantWageAndBillingRate({
        task, user, costcode, unionClass,
      });

      let wage = resolvedHourlyWage ?? 0;
      let dailyWage = 0;
      // Daily wage is flat per user+date+costcode; only charge once per combo
      if (resolvedDailyWage != null) {
        wage = 0;
        if (!userDateCostCodeSet.has(userDateCostCodeKey)) {
          dailyWage = resolvedDailyWage;
          userDateCostCodeSet.add(userDateCostCodeKey);
        }
      }

      if (!(aggKey in userObj.values)) {
        userObj.values[aggKey] = {
          employeeId: user.employeeId || '',
          regularHours: 0,
          otHours: 0,
          totalHours: 0,
          ptoHours: 0,
          regOT: 0,
          doubleOT: 0,
          saturday: 0,
          saturdayOT: 0,
          saturdayDoubleOT: 0,
          onlyRegularOT: 0,
          onlyDoubleOT: 0,
          sunday: 0,
          sundayOT: 0,
          sundayDoubleOT: 0,
          // Night shift hours tracked separately for cost calculation
          regularHoursNightShift: 0,
          regOTNightShift: 0,
          doubleOTNightShift: 0,
          saturdayNightShift: 0,
          saturdayOTNightShift: 0,
          saturdayDoubleOTNightShift: 0,
          sundayNightShift: 0,
          sundayOTNightShift: 0,
          sundayDoubleOTNightShift: 0,
          onlyRegularOTNightShift: 0,
          onlyDoubleOTNightShift: 0,
          hasNightShift: false,
          costcode: costcode ? costcode.code : null,
          project: projectLabel,
          [date]: 0,
          key: user.id + aggKey,
          serviceLocation: task.serviceLocation,
          division: getDivision({
            project, divisionMap, userDivisions, user,
          }),
          wage,
          dailyWage,
          weightedAverageWage: 0,
          totalDailyCost: 0,
          userSettings: entityOTProfileMap[user.id] ?? settings,
        };
      }

      const aggValue = userObj.values[aggKey];

      const hrReg = msToHours(task.regularTime);
      const hourOT = msToHours(task.overtime);
      const hourDouble = msToHours(task.doubleOT);
      const hourPTO = msToHours(task.pto || 0);
      const satHrReg = msToHours(task.saturdayTime || 0);
      const satHourOT = msToHours(task.saturdayOT || 0);
      const satHourDouble = msToHours(task.saturdayDoubleOT || 0);
      const sunHrReg = msToHours(task.sundayTime || 0);
      const sunHourOT = msToHours(task.sundayOT || 0);
      const sunHourDouble = msToHours(task.sundayDoubleOT || 0);

      const aggTotalHours = (
        hrReg + hourOT + hourDouble + hourPTO
        + satHrReg + sunHrReg + satHourOT + satHourDouble
        + sunHourOT + sunHourDouble
      );

      const aggReg = (
        hrReg + satHrReg + sunHrReg
      );

      const aggOTReg = (
        hourOT + satHourOT + sunHourOT
      );

      const aggOTDouble = (
        hourDouble + satHourDouble + sunHourDouble
      );

      if (task.isNightShift) {
        aggValue.hasNightShift = true;
        aggValue.regularHoursNightShift += aggReg;
        aggValue.regOTNightShift += aggOTReg;
        aggValue.doubleOTNightShift += aggOTDouble;
        aggValue.saturdayNightShift += satHrReg;
        aggValue.saturdayOTNightShift += satHourOT;
        aggValue.saturdayDoubleOTNightShift += satHourDouble;
        aggValue.sundayNightShift += sunHrReg;
        aggValue.sundayOTNightShift += sunHourOT;
        aggValue.sundayDoubleOTNightShift += sunHourDouble;
        aggValue.onlyRegularOTNightShift += hourOT;
        aggValue.onlyDoubleOTNightShift += hourDouble;
      }

      // Display fields always accumulate all hours (night shift + regular combined)
      aggValue.regularHours += aggReg;
      aggValue.otHours += aggOTReg + aggOTDouble;
      aggValue.totalHours += aggTotalHours;
      aggValue.regOT += aggOTReg;
      aggValue.doubleOT += aggOTDouble;
      aggValue.ptoHours += hourPTO;

      aggValue.onlyRegularOT += hourOT;
      aggValue.onlyDoubleOT += hourDouble;

      aggValue.saturdayOT += satHourOT;
      aggValue.saturdayDoubleOT += satHourDouble;
      aggValue.sundayOT += sunHourOT;
      aggValue.sundayDoubleOT += sunHourDouble;

      aggValue.weightedAverageWage += wage * totalHours;
      aggValue.totalDailyCost += dailyWage;

      if (!(date in aggValue)) {
        aggValue[date] = 0;
      }
      aggValue[date] += totalHours;
    });
  });

  // TODO: REFACTOR
  Object.values(dateAggregate).forEach((obj) => {
    Object.values(obj.values).forEach((hours) => {
      const relevantWage = hours.totalHours
        ? hours.weightedAverageWage / hours.totalHours
        : 0;

      // Subtract night shift hours from display fields to avoid double-counting;
      // night shift cost is calculated separately with the premium applied.
      const regularHoursCost = calculateCostFromHours({
        ...hours,
        regularHours: hours.regularHours - hours.regularHoursNightShift,
        regOT: hours.onlyRegularOT - hours.onlyRegularOTNightShift,
        doubleOT: hours.onlyDoubleOT - hours.onlyDoubleOTNightShift,
        saturday: hours.saturday - hours.saturdayNightShift,
        saturdayOT: hours.saturdayOT - hours.saturdayOTNightShift,
        saturdayDoubleOT: hours.saturdayDoubleOT - hours.saturdayDoubleOTNightShift,
        sunday: hours.sunday - hours.sundayNightShift,
        sundayOT: hours.sundayOT - hours.sundayOTNightShift,
        sundayDoubleOT: hours.sundayDoubleOT - hours.sundayDoubleOTNightShift,
      }, relevantWage, hours.userSettings);

      const wageWithNightShift = applyNightShiftPremium(
        relevantWage,
        { isNightShift: true },
        hours.userSettings,
      );

      const nightShiftHoursCost = calculateCostFromHours({
        regularHours: hours.regularHoursNightShift,
        regOT: hours.onlyRegularOTNightShift,
        doubleOT: hours.onlyDoubleOTNightShift,
        saturday: hours.saturdayNightShift,
        saturdayOT: hours.saturdayOTNightShift,
        saturdayDoubleOT: hours.saturdayDoubleOTNightShift,
        sunday: hours.sundayNightShift,
        sundayOT: hours.sundayOTNightShift,
        sundayDoubleOT: hours.sundayDoubleOTNightShift,
        ptoHours: 0,
      }, wageWithNightShift, hours.userSettings);

      let totalCost = regularHoursCost + nightShiftHoursCost + hours.totalDailyCost;

      if (Number.isNaN(totalCost)) totalCost = 0;
      const computeWage = hours.totalHours
        ? totalCost / hours.totalHours
        : (hours.wage ?? 0);

      const wage = `$${computeWage.toFixed(2)}`;

      const dataObject = {
        name: obj.name,
        employeeId: obj.employeeId,
        division: hours.division,
        project: hours.project,
        costcode: hours.costcode,
        regularHours: hours.regularHours.toFixed(2),
        ptoHours: hours.ptoHours.toFixed(2),
        otHours: hours.otHours.toFixed(2),
        totalHours: hours.totalHours.toFixed(2),
        key: hours.key,
        serviceLocation: hours.serviceLocation,

        // These four fields are not displayed in the report but used for the export
        regHoursRaw: hours.regularHours,
        regularOTRaw: hours.regOT,
        doubleOTRaw: hours.doubleOT,
        ptoHoursRaw: hours.ptoHours,
        ptoRow: hours.ptoHours,
        wage,
        rawTotalCost: totalCost,
      };

      dates.forEach((dateS) => {
        if (dateS in hours) {
          dataObject[dateS] = hours[dateS].toFixed(2);
        }
      });
      data.push(dataObject);
    });
  });

  onDataChanged(data);
  onFiltersChanged({
    users: filteredUsers,
    projects,
    costcodes,
  });
  return data;
};
